import {
  mutationOptions,
  queryOptions,
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { type QueryError } from 'src/queries/network.utils'

import {
  completeVendorAccountCollectionFlow,
  confirmVendorAccountCollectionFlow,
  createVendor,
  createVendorAccountCollectionFlow,
  getVendor,
  getVendors,
} from './vendors.network'
import {
  CompleteVendorAccountCollectionFlowInput,
  ConfirmVendorAccountCollectionFlowInput,
  CreateVendorMutationInput,
  ListVendorsParams,
  Vendor,
  VendorAccountCollectionFlowCompletionResponse,
  VendorAccountCollectionFlowConfirmationResponse,
  VendorAccountCollectionFlowResponse,
} from './vendors.types'

export const vendorKeys = {
  lists: (organizationId: string | undefined) =>
    ['vendors', 'list', organizationId] as const,
  list: (organizationId: string | undefined, params: ListVendorsParams) =>
    [...vendorKeys.lists(organizationId), params] as const,
  details: (organizationId: string | undefined) =>
    ['vendors', 'detail', organizationId] as const,
  detail: (organizationId: string | undefined, vendorId: string | undefined) =>
    [...vendorKeys.details(organizationId), vendorId] as const,
}

export const vendorQueries = {
  list: (organizationId: string | undefined, params: ListVendorsParams) =>
    queryOptions({
      queryKey: vendorKeys.list(organizationId, params),
      queryFn: () => getVendors(params),
      enabled: Boolean(organizationId),
    }),
  detail: (organizationId: string | undefined, vendorId: string | undefined) =>
    queryOptions({
      queryKey: vendorKeys.detail(organizationId, vendorId),
      queryFn: () => getVendor(vendorId!),
      enabled: Boolean(organizationId && vendorId),
    }),
}

export const vendorMutations = {
  create: () =>
    mutationOptions<Vendor, QueryError, CreateVendorMutationInput>({
      mutationFn: (input) => createVendor(input),
    }),
  createAccountCollectionFlow: () =>
    mutationOptions<VendorAccountCollectionFlowResponse, QueryError, string>({
      mutationFn: (vendorId) => createVendorAccountCollectionFlow(vendorId),
    }),
  completeAccountCollectionFlow: () =>
    mutationOptions<
      VendorAccountCollectionFlowCompletionResponse,
      QueryError,
      CompleteVendorAccountCollectionFlowInput
    >({
      mutationFn: (input) => completeVendorAccountCollectionFlow(input),
    }),
  confirmAccountCollectionFlow: () =>
    mutationOptions<
      VendorAccountCollectionFlowConfirmationResponse,
      QueryError,
      ConfirmVendorAccountCollectionFlowInput
    >({
      mutationFn: (input) => confirmVendorAccountCollectionFlow(input),
    }),
}

type CreateVendorMutationOptions = Omit<
  UseMutationOptions<Vendor, QueryError, CreateVendorMutationInput>,
  'mutationFn'
>

type ConfirmVendorMutationOptions = Omit<
  UseMutationOptions<
    VendorAccountCollectionFlowConfirmationResponse,
    QueryError,
    ConfirmVendorAccountCollectionFlowInput
  >,
  'mutationFn'
>

export function useCreateVendor(
  organizationId: string,
  options?: CreateVendorMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...vendorMutations.create(),
    ...restOptions,
    onSuccess: (vendor, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.lists(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, vendor.id),
        exact: true,
      })
      onSuccess?.(vendor, variables, onMutateResult, context)
    },
  })
}

export function useCreateVendorAccountCollectionFlow() {
  return useMutation(vendorMutations.createAccountCollectionFlow())
}

export function useCompleteVendorAccountCollectionFlow(
  organizationId: string,
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...vendorMutations.completeAccountCollectionFlow(),
    onSuccess: (_response, variables) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, variables.vendorId),
        exact: true,
      })
    },
  })
}

export function useVendorDetail(
  organizationId: string | undefined,
  vendorId: string | undefined,
) {
  return useQuery(vendorQueries.detail(organizationId, vendorId))
}

export function useConfirmVendorAccountCollectionFlow(
  organizationId: string,
  options?: ConfirmVendorMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...vendorMutations.confirmAccountCollectionFlow(),
    ...restOptions,
    onSuccess: (response, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.lists(organizationId),
      })
      void queryClient.invalidateQueries({
        queryKey: vendorKeys.detail(organizationId, variables.vendorId),
        exact: true,
      })
      onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
